/**
 * Skill validation and content moderation utilities
 * Detects obscene, spam, and non-skills-related words
 */

// List of obscene/profane words (expandable)
const OBSCENE_WORDS = [
  'damn', 'crap', 'hell', 'bitch', 'bastard', 'ass', 'asshole',
  'fuck', 'shit', 'piss', 'cock', 'dick', 'pussy', 'whore', 'slut',
  'motherfucker', 'fuckwit', 'clusterfuck', 'shitty', 'shithole',
  'bullshit', 'horseshit', 'apeshit', 'batshit', 'jackass',
];

// List of spam/non-skills-related keywords
const SPAM_KEYWORDS = [
  'buy', 'sell', 'free money', 'earn money', 'work from home',
  'click here', 'visit', 'website', 'casino', 'lottery',
  'viagra', 'cialis', 'dating', 'porn', 'xxx',
  'crypto', 'bitcoin', 'nft', 'eth', 'forex',
  'mlm', 'pyramid', 'get rich', 'guaranteed income',
];

// List of generic non-skills words
const NON_SKILLS_WORDS = [
  'test', 'asdf', 'qwerty', 'aaa', 'bbb', 'ccc', 'xxx',
  'foo', 'bar', 'lorem ipsum', 'placeholder', 'demo',
  'nothing', 'no skill', 'none', 'na', 'n/a',
  '123', '456', '789', '...', '___',
];

/**
 * Check if a skill contains obscene, spam, or invalid words
 * @param {string} skillName - The skill name to validate
 * @returns {Object} Object with flagged status and reason
 */
export const validateSkillContent = (skillName) => {
  if (!skillName || typeof skillName !== 'string') {
    return { isFlagged: false, reason: null };
  }

  const lowerSkill = skillName.toLowerCase().trim();

  // Check for obscene words
  for (const word of OBSCENE_WORDS) {
    if (lowerSkill.includes(word)) {
      return {
        isFlagged: true,
        reason: 'obscene',
        message: 'Contains inappropriate language'
      };
    }
  }

  // Check for spam keywords
  for (const keyword of SPAM_KEYWORDS) {
    if (lowerSkill.includes(keyword)) {
      return {
        isFlagged: true,
        reason: 'spam',
        message: 'Contains spam or advertising content'
      };
    }
  }

  // Check for non-skills content
  for (const nonSkill of NON_SKILLS_WORDS) {
    if (lowerSkill === nonSkill) {
      return {
        isFlagged: true,
        reason: 'invalid',
        message: 'Not a valid skill'
      };
    }
  }

  // Check for suspicious patterns (all numbers, all special chars, etc.)
  if (/^[0-9\s\-_.]*$/.test(skillName) && skillName.length > 0) {
    return {
      isFlagged: true,
      reason: 'invalid',
      message: 'Skill contains only numbers or special characters'
    };
  }

  // Check for minimum length (skills should be at least 2 characters and meaningful)
  if (skillName.length < 2) {
    return {
      isFlagged: true,
      reason: 'invalid',
      message: 'Skill name too short'
    };
  }

  return { isFlagged: false, reason: null };
};

/**
 * Get the flag color based on reason
 * @param {string} reason - The reason for flagging
 * @returns {string} Color classes
 */
export const getFlagColor = (reason) => {
  switch (reason) {
    case 'obscene':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'spam':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'invalid':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

/**
 * Get the flag badge text
 * @param {string} reason - The reason for flagging
 * @returns {string} Badge text
 */
export const getFlagBadge = (reason) => {
  switch (reason) {
    case 'obscene':
      return '⚠️ Inappropriate';
    case 'spam':
      return '🚫 Spam';
    case 'invalid':
      return '❌ Invalid';
    default:
      return '📋 Flagged';
  }
};

/**
 * Add new obscene words to the validation list
 * @param {string[]} newWords - Array of new words to add
 */
export const addObsceneWords = (newWords) => {
  if (Array.isArray(newWords)) {
    OBSCENE_WORDS.push(...newWords.map(w => w.toLowerCase().trim()));
  }
};

/**
 * Add new spam keywords to the validation list
 * @param {string[]} newKeywords - Array of new keywords to add
 */
export const addSpamKeywords = (newKeywords) => {
  if (Array.isArray(newKeywords)) {
    SPAM_KEYWORDS.push(...newKeywords.map(k => k.toLowerCase().trim()));
  }
};
